import FormLabel from '@rio-cloud/rio-uikit/FormLabel';
import { getTrackingAttributes } from '@rio-cloud/rio-uikit/googleAnalyticsUtils';
import classNames from 'classnames';
import type React from 'react';
import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, useState } from 'react';
import { type Control, Controller } from 'react-hook-form';
import { FormattedMessage, type IntlShape } from 'react-intl';

import { TextareaField } from '../../form/ControlledTextarea.tsx';

type CommentInputProps<Element = InputHTMLAttributes<HTMLInputElement>> = Element & {
    name: string;
    // biome-ignore lint/suspicious/noExplicitAny: <react-hook-form> types
    control: Control<any>;
    intl: IntlShape;
    className?: string;
    showInitially?: boolean;
    inputGroupClassName?: string;
    useFormLabel?: boolean;
    removable?: boolean;
};
export const ControlledCommentInput = forwardRef<
    HTMLTextAreaElement,
    CommentInputProps<TextareaHTMLAttributes<HTMLTextAreaElement>>
>(
    (
        {
            name,
            control,
            intl,
            className,
            showInitially = false,
            inputGroupClassName = 'margin-y-10',
            useFormLabel = false,
            removable = true,
            ...props
        },
        ref
    ) => {
        const [isCommentVisible, setIsCommentVisible] = useState(showInitially || !removable);
        const id = `form_id_${name}`;

        return (
            <Controller
                render={({ field, fieldState: { error, invalid } }) => {
                    return (
                        <div
                            className={classNames('form-group', className, {
                                'has-error': invalid,
                                'has-feedback': invalid,
                            })}
                        >
                            <CommentLabel htmlFor={id} enabled={useFormLabel}>
                                <FormattedMessage
                                    id={
                                        isCommentVisible
                                            ? 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.comment-remove'
                                            : 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.comment-link'
                                    }
                                    values={{
                                        link: (chunks: React.ReactNode) =>
                                            removable ? (
                                                <a
                                                    onClick={event => {
                                                        event.preventDefault();
                                                        if (isCommentVisible) {
                                                            field.onChange('');
                                                        }
                                                        setIsCommentVisible(!isCommentVisible);
                                                    }}
                                                    {...getTrackingAttributes({
                                                        trigger: 'click',
                                                        category: 'link autoTracking',
                                                        action: 'link click',
                                                        label: isCommentVisible
                                                            ? 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.comment-remove'
                                                            : 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.comment-link',
                                                    })}
                                                >
                                                    {chunks}
                                                </a>
                                            ) : null,
                                    }}
                                />
                            </CommentLabel>
                            {isCommentVisible && (
                                <div className={classNames('input-group', inputGroupClassName)}>
                                    <span className='input-group-addon'>
                                        <span className={'uikit-icon  icon-comment'} />
                                    </span>

                                    <TextareaField
                                        {...props}
                                        field={field}
                                        error={error?.message}
                                        id={id}
                                        forwardedRef={ref}
                                        wrapInputGroup={false}
                                    />
                                </div>
                            )}
                        </div>
                    );
                }}
                name={name}
                control={control}
            />
        );
    }
);

const CommentLabel = ({
    htmlFor,
    enabled,
    children,
}: {
    htmlFor: string;
    enabled: boolean;
    children: React.ReactNode;
}) => (enabled ? <FormLabel htmlFor={htmlFor}>{children}</FormLabel> : children);
